/**
 * Custom Event Text — Domain API Module
 *
 * Wraps the shared V2 API client for all data operations on
 * alert notification format templates (core_event_text_template).
 *
 * Stored procedures:
 *   getalerttemplates — read all templates for a campus
 *
 * Table:
 *   core_event_text_template
 *
 * CRUD via apiSave (mysave endpoint):
 *   PUT    — add template
 *   POST   — edit template
 *   DELETE  — delete template(s)
 */

import { apiFetch, apiSave } from '../../../../services/api.js';

// ---------------------------------------------------------------------------
// READ
// ---------------------------------------------------------------------------

/** Fetch all alert text templates for a campus. */
export function fetchTemplates(campusId) {
  return apiFetch({
    procedure: 'getalerttemplates',
    reqType: 'stored',
    pKey: 'campus_id',
    pVal: campusId,
  });
}

// ---------------------------------------------------------------------------
// WRITE
// ---------------------------------------------------------------------------

/**
 * Add a new alert text template.
 * @param {{ output_type_id: number, campus_id: number, text_format: string, modified_by: number }} payload
 * @param {object} session — { campusId, userId, clientId }
 */
export function addTemplate(payload, { campusId, userId, clientId }) {
  return apiSave({
    table: 'core_event_text_template',
    payload: [payload],
    pKey: 'template_id',
    condition: 'primary',
    campusId,
    userId,
    clientId,
    method: 'PUT',
  });
}

/**
 * Edit an existing alert text template.
 * @param {object} payload — new values { template_id, campus_id, text_format }
 * @param {object} oldPayload — previous values for audit { template_id, campus_id, text_format }
 * @param {object} session — { campusId, userId, clientId }
 */
export function editTemplate(payload, oldPayload, { campusId, userId, clientId }) {
  return apiSave({
    table: 'core_event_text_template',
    payload: [payload],
    oldPayload: [oldPayload],
    pKey: 'template_id',
    condition: 'primary',
    campusId,
    userId,
    clientId,
    method: 'POST',
  });
}

/**
 * Delete one or more alert text templates.
 * @param {Array<{ template_id: number }>} templateIds
 * @param {object} session — { campusId, userId, clientId }
 */
export function deleteTemplates(templateIds, { campusId, userId, clientId }) {
  return apiSave({
    table: 'core_event_text_template',
    payload: templateIds,
    pKey: 'template_id',
    condition: 'primary',
    campusId,
    userId,
    clientId,
    method: 'DELETE',
  });
}
